import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { SearchComponent, SearchState } from '@nx-ahs/search';
import { AHSTripettoComponent } from '@nx-ahs/script-collector';
import { Value } from 'tripetto-runner-foundation';
import { DynamicScriptingService } from '@nx-ahs/script-shared/services';

@Component({
	selector: 'custom-search',
	templateUrl: './custom-search.component.html',
	styleUrls: ['./custom-search.component.scss'],
	standalone: false
})
export class CustomSearchComponent extends AHSTripettoComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild(SearchComponent)
	set searchComponentStuff(input: SearchComponent) {
		if (!input) return;

		this.searchComponent = input;
		this.searchState = input.searchState;

		if (this.readonly) {
			this.searchComponent.disabled = true;
		}
	}

	@Input()
	searchOnLoad: boolean = false;

	@Input()
	showCriteria: boolean = true;

	@Input()
	saveStateKey: string;

	@Input()
	searchId?: number = 21;

	@Input()
	criteriaValues: any = {};

	@Input()
	requiredValues: any = {};

	@Input()
	displayMode: string = 'table';

	@Input()
	useSpecificCriteriaLabel: boolean = true;

	@Input()
	criteriaLabel: string = 'Search';

	@Input()
	skipAutoClickPersonAfterSearch: boolean = true;

	@Input()
	isMainSearch: boolean = false;

	@Input()
	showAdditionalButtons: boolean = false;

	@Input()
	showButtons: boolean = true;

	@Input()
	selectedIdField: string = 'id';

	@Input()
	canFilterText: boolean = true;

	@Input()
	canExport: boolean = true;

	@Input()
	canCustomizeView: boolean = true;

	@Input()
	healthTrackMode: boolean = false;

	@Input()
	hidePagination: boolean = false;

	@Output()
	onSearchStateChanged = new EventEmitter<any>();

	@Output()
	onSelected = new EventEmitter<any>();

	public isReady = false;
	public serviceSearchSettings: any = null;
	public selected: any;
	public searchState: SearchState;
	public searchComponent: SearchComponent;

	private tripSubscription: Value;
	private manuallySelected = false;

	constructor(private scriptService: DynamicScriptingService) {
		super();
	}

	ngOnInit(): void {
		this.scriptService.addOrUpdateRenderedControl(this.blockInfo.id, false);
	}

	ngAfterViewInit() {
		this.waitTillReady()
			.then(() => {
				// subscribe to changes and preload
				this.tripSubscription = this.blockInfo.componentSlot.subscribe((data) => {
					if (data?.string && !this.manuallySelected) {
						this.tripSubscription.unsubscribe();
						return this.checkAndSetPreloadedValue(data.string);
					}
				});

				// preload
				if (this.getTripettoValue()) {
					return this.checkAndSetPreloadedValue(this.getTripettoValue());
				}
			})
			.finally(() => {
				this.scriptService.addOrUpdateRenderedControl(this.blockInfo.id, true);
			});
	}

	ngOnDestroy() {
		this.tripSubscription?.unsubscribe();
	}

	public searchStateChanged(criteria: any): void {
		this.onSearchStateChanged.emit(criteria);
	}

	public selectedRowsChanged(selectedModel: SelectionModel<any>): void {
		const selected = selectedModel.selected[selectedModel.selected.length - 1] ?? null;

		if (selected) {
			this.manuallySelected = true;
			this.setTripettoValue(JSON.stringify(selected));
		} else {
			this.setTripettoValue(null);
		}

		this.onSelected.emit(selected);
	}

	public selectedCardChanged(results: any): void {
		if (results != null && typeof results == 'object') {
			this.manuallySelected = true;
			this.setTripettoValue(JSON.stringify(results));
		} else {
			this.setTripettoValue(null);
		}
	}

	private checkAndSetPreloadedValue(val: string): Promise<void> {
		let sec = 0;
		const valObj = JSON.parse(val);

		return new Promise((resolve, reject) => {
			const interval = setInterval(() => {
				sec = sec + 1;

				if (this.searchState?.results?.data) {
					let matchingIndex;

					const match = this.searchState?.results?.data.find((obj, i) => {
						if (obj[`${this.selectedIdField}`] == valObj[`${this.selectedIdField}`]) {
							matchingIndex = i;
							return true;
						}
					});

					this.searchComponent.rowClickedLocal(match, matchingIndex);

					clearInterval(interval);
					resolve();
				} else if (sec >= 5) {
					clearInterval(interval);
					resolve();
				}
			}, 1000);
		});
	}

	private waitTillReady(): Promise<void> {
		return new Promise((resolve, reject) => {
			if (this.scriptJsonStr) {
				const intervalB = setInterval(() => {
					const obj = JSON.parse(this.scriptJsonStr);

					// check if all props have a value
					if (!this.checkEmpty(obj)) {
						// all props have a value
						this.isReady = true;
						clearInterval(intervalB);
						resolve();
					}
				}, 1000);
			} else {
				this.isReady = true;
				resolve();
			}
		});
	}

	private checkEmpty(obj): boolean {
		for (const key in obj) {
			//if the value is 'object'
			if (obj[key] instanceof Object === true) {
				if (this.checkEmpty(obj[key]) === false) {
					return false;
				}
			}
			//if value is string/number
			else {
				//if array or string have length is not 0.
				if (obj[key].length !== 0) return false;
			}
		}
		return true;
	}
}
